import React, { useState } from 'react';
import { Calendar, X, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookCallModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    agency_name: '',
    email: '',
    phone: '',
    role: '',
    package: '',
    preferred_date: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const packages = [
    'Start Zone',
    'Flusso Attivo', 
    'Team Esperto'
  ];

  const roles = [
    'Titolare',
    'HR',
    'Manager'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Converti la data in formato ISO
      const appointmentData = {
        ...formData,
        preferred_date: new Date(formData.preferred_date).toISOString()
      };

      const response = await axios.post(`${API}/appointments`, appointmentData);
      
      if (response.data.success) {
        setSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setSuccess(false);
          resetForm();
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setError('Errore nell\'invio della richiesta. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      agency_name: '',
      email: '',
      phone: '',
      role: '',
      package: '',
      preferred_date: '',
      message: ''
    });
    setError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  // Success state
  if (success) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">✅ Grazie!</h2>
            <p className="text-gray-600 mb-2">
              La tua richiesta è stata inviata con successo.
            </p>
            <p className="text-sm text-gray-500">
              Riceverai una conferma via email entro 24 ore dal Team InLeads.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl font-bold text-gray-900">
            <Calendar className="mr-3 h-6 w-6 text-[#2563EB]" />
            Prenota una Call Strategica
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-medium">Errore</p>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome e Cognome *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Mario Rossi"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agency_name">Nome Agenzia *</Label>
              <Input
                id="agency_name"
                type="text"
                value={formData.agency_name}
                onChange={(e) => setFormData(prev => ({ ...prev, agency_name: e.target.value }))}
                placeholder="Re/Max Alpha"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="mario.rossi@remax.it"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+39 345 1234567"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role">Ruolo *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona il tuo ruolo" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="package">Pacchetto d'interesse *</Label>
              <Select value={formData.package} onValueChange={(value) => setFormData(prev => ({ ...prev, package: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona pacchetto" />
                </SelectTrigger>
                <SelectContent>
                  {packages.map(pkg => (
                    <SelectItem key={pkg} value={pkg}>
                      {pkg}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="preferred_date">Data e Ora Preferita *</Label>
            <Input
              id="preferred_date"
              type="datetime-local"
              value={formData.preferred_date}
              onChange={(e) => setFormData(prev => ({ ...prev, preferred_date: e.target.value }))}
              min={new Date().toISOString().slice(0, 16)}
              required
            />
            <p className="text-xs text-gray-500">Seleziona giorno e orario desiderato per la call</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Messaggio (opzionale)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Descrivi brevemente le tue esigenze o domande specifiche..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white px-8 py-2 font-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Invio...
                </>
              ) : (
                <>
                  <Calendar className="mr-2 h-4 w-4" />
                  📅 Richiedi la tua Call Strategica
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookCallModal;